import { useEffect, useMemo } from 'react';

import { useTelemetry } from '@order/Telemetry';

import {
  useCustomerData,
  useHasLoggedOut,
  useIsLoggingIn,
} from '../../context/AuthMachine';
import { useBraze } from '../../context/Braze';
import { useLaunchDarklyIntegration } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Initializes third-party vendors integrations and synchronizes current customer data
 */
export const use3rdPartyIntegrations = () => {
  const hasLoggedOut = useHasLoggedOut();
  const isLoggingIn = useIsLoggingIn();

  const customerData = useCustomerData();
  const { id, trackingUuid, firstName, lastName, email } = customerData ?? {};

  // NOTE: We extract required customer data for 3rd party vendors to prevent
  //       extra effects
  const customer = useMemo(
    () => ({ id, trackingUuid, firstName, lastName, email }),
    [id, trackingUuid, firstName, lastName, email],
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  // NOTE: WE extract helpers individually to prevent unnecessary effects
  //       because of internal state updates

  const {
    initialize: initTelemetry,
    identify: syncTelemetryCustomer,
    reset: resetTelemetryCustomer,
  } = useTelemetry();

  const {
    init: initBraze,
    destroy: destroyBraze,
    syncCustomer: syncBrazeCustomer,
  } = useBraze();

  // ─── Launch Darkly ───────────────────────────────────────────────────

  useLaunchDarklyIntegration({ customer, isLoggingIn, hasLoggedOut });

  // ─── Braze ───────────────────────────────────────────────────────────

  useEffect(() => {
    initBraze?.();

    return () => {
      destroyBraze?.();
    };
  }, [destroyBraze, initBraze]);

  useEffect(() => {
    if (!customer.id) return;

    syncBrazeCustomer(customer);
  }, [customer, syncBrazeCustomer]);

  // ─── Telemetry ───────────────────────────────────────────────────────

  useEffect(() => {
    void initTelemetry();
  }, [initTelemetry]);

  useEffect(() => {
    if (!customer.id) return;

    syncTelemetryCustomer(customer);

    return () => {
      resetTelemetryCustomer();
    };
  }, [customer, resetTelemetryCustomer, syncTelemetryCustomer]);
};
